/* eslint-disable @typescript-eslint/no-throw-literal */
import { AuthenticatedAppShell } from '@/components/authenticatedAppShell';
import { signOut, useAuth } from '@/features/auth/store/authStore';
import { isProfileComplete } from '@/features/auth/utils/isProfileComplete';
import { growthbook } from '@/libs/growthbook';
import { canAccessInvestorType } from '@/features/misc/utils/canAccessInvestorType';
import {
  getInvestmentDetailsQueryOptions,
  getInvestorDeclarationSuspenseQueryOptions,
  getListUserObjectsSuspenseQueryOptions,
  getReferralsSuspenseQueryOptions,
  useGetInvestmentDetailsSuspense,
  useGetInvestorDeclarationSuspense,
  useGetListUserObjectsSuspense,
  useGetReferralsSuspense,
} from '@repo/api/services/userBackend/gen';
import { isTenant } from '@repo/utils';
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';

import { canAccessAccountSettings } from './_authenticated/_pfida/account-settings';
import { canAccessDocument } from './_authenticated/_pfida/documents';
import { canAccessInvestorHub } from './_authenticated/_pfida/investor-hub';
import { canAccessReferrals } from './_authenticated/_pfida/referrals';

const AuthenticatedSearchQueryValidation = z.object({
  referral: z.string().optional(),
  company: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated')({
  validateSearch: AuthenticatedSearchQueryValidation,
  beforeLoad: async ({ location, search }) => {
    const { isAuthenticated, user } = useAuth.getState();

    if (!isAuthenticated()) {
      signOut();
      throw redirect({
        to: '/login',
        search: {
          to: location.href,
          referral: search.referral,
          company: search.company,
        },
      });
    }

    // TT-137: force admin-created investors (no birthdate/phone) to complete
    // their profile before reaching any product. Flag-gated and only fires for
    // incomplete accounts; every self-signup is complete, so this is a no-op
    // for existing customers. Exclude the completion route itself to avoid a
    // redirect loop.
    //
    // GrowthbookProvider kicks off init() in a (non-awaited) effect, so on a
    // cold / direct navigation this beforeLoad can run before features have
    // loaded and isOn() would fail open (return false). Await readiness here
    // before reading the flag. init() is idempotent and resolves from cache on
    // subsequent calls, and this only runs on authenticated routes so it adds
    // no latency to login / signup.
    if (!growthbook.ready) {
      await growthbook.init();
    }

    if (
      growthbook.isOn('enable-wakala-profile-completion') &&
      !isProfileComplete(user) &&
      !location.pathname.startsWith('/complete-profile')
    ) {
      throw redirect({ to: '/complete-profile' });
    }
  },
  loader: ({ context: { queryClient } }) => {
    const { isAuthenticated } = useAuth.getState();

    if (isAuthenticated()) {
      queryClient.ensureQueryData(getListUserObjectsSuspenseQueryOptions());
      queryClient.ensureQueryData(getReferralsSuspenseQueryOptions());
      queryClient.ensureQueryData(getInvestorDeclarationSuspenseQueryOptions());
      queryClient.ensureQueryData(getInvestmentDetailsQueryOptions());
    }
  },
  component: () => <Authenticated />,
});

function Authenticated() {
  const userListObjects = useGetListUserObjectsSuspense();
  const accessInvestorDeclaration = useGetInvestorDeclarationSuspense();
  const referrals = useGetReferralsSuspense();
  const investmentDetails = useGetInvestmentDetailsSuspense();

  const numberOfUnreadDocuments = useMemo(() => {
    if (userListObjects.data.data.any_unread) {
      return userListObjects.data.data.user_objects_v2?.length;
    }

    return 0;
  }, [userListObjects.data.data.any_unread, userListObjects.data.data.user_objects_v2?.length]);

  const routesToShow = useMemo(
    () => ({
      '/dashboard': true,
      '/investor_type': canAccessInvestorType(accessInvestorDeclaration.data.data.action),
      '/referrals': canAccessReferrals(referrals.data.data.show_referrals),
      '/documents': canAccessDocument(),
      '/account-settings': canAccessAccountSettings(),
      '/investor-hub': canAccessInvestorHub(investmentDetails.data.data.pfidac),
      help: isTenant('pfida'),
    }),
    [accessInvestorDeclaration.data.data.action, investmentDetails.data.data.pfidac, referrals.data.data.show_referrals]
  );

  return (
    <AuthenticatedAppShell numberOfDocuments={numberOfUnreadDocuments} routesToShow={routesToShow}>
      <Outlet />
    </AuthenticatedAppShell>
  );
}
